import ENV from "config/Env";
import { typedQueryClient, useTypedMutation } from "hooks";
import { USER_STATUSES } from "models";
import { useBrandGroupsQuery } from "modules/BrandsDashboard/hooks";
import { getBrandGroupsByIds } from "modules/BrandsDashboard/utils";
import { useUserDetailsQuery } from "modules/UsersDashboard/hooks";
import { UsersDashboardQueryKeys } from "modules/UsersDashboard/queryKeys";
import { ApiInstance } from "utils/Api";

import type { CreateEditUserForm } from "modules/UsersDashboard/components";

export const useEditUserMutation = (userId: number | null) => {
  const { data: userDetails } = useUserDetailsQuery(userId);
  const { data: brandGroups = [] } = useBrandGroupsQuery();

  const constructPayload = (params: CreateEditUserForm) => {
    const { isActive, confirmPassword, password, onei_planning_monteur_id, planit_planning_mechanic_id, dms_nr, ...rest } = params;
    const isPasswordUpdated = password && confirmPassword && password === confirmPassword;

    return {
      ...userDetails,
      ...rest,
      ...(isPasswordUpdated && { password }),
      brand_groups: getBrandGroupsByIds(rest.brand_group_ids, brandGroups),
      status: isActive ? USER_STATUSES.UserActive : USER_STATUSES.UserInactive,
      onei_planning_monteur_id: onei_planning_monteur_id || null,
      planit_planning_mechanic_id: planit_planning_mechanic_id || null,
      dms_nr: dms_nr || null,
      dealer_id: rest.dealer_id ?? userDetails?.dealer_id ?? -1
    };
  };

  return useTypedMutation<CreateEditUserForm, void>({
    mutationFn: async payload => (await ApiInstance.post(`/users/update`, constructPayload(payload), ENV.systemBaseURL)).data,
    onSuccess: payload => {
      const previousUsers = typedQueryClient.getQueryData(UsersDashboardQueryKeys.users);
      if (!previousUsers) return;

      const userIdx = previousUsers.users.findIndex(user => user.id === userId);
      if (userIdx === -1) return;

      const previousUser = previousUsers.users[userIdx];
      const { password: _password, confirmPassword: _confirmPassword, ...rest } = payload;

      typedQueryClient.setQueryData(UsersDashboardQueryKeys.users, {
        ...previousUsers,
        users: previousUsers.users.with(userIdx, {
          ...previousUser,
          ...rest,
          brand_groups: getBrandGroupsByIds(payload.brand_group_ids, brandGroups),
          status: payload.isActive ? USER_STATUSES.UserActive : USER_STATUSES.UserInactive,
          phone: payload.phone ?? previousUser.phone,
          role_id: payload.role_id ?? previousUser.role_id,
          dealer_id: payload.dealer_id ?? previousUser.dealer_id,
          dealer_location_id: payload.dealer_location_id ?? previousUser.dealer_location_id,
          dealers_ids: payload.dealers_ids ?? previousUser.dealers_ids,
          location_ids: payload.location_ids ?? previousUser.location_ids,
          dms_nr: payload.dms_nr || null,
          onei_planning_monteur_id: payload.onei_planning_monteur_id ?? undefined,
          planit_planning_mechanic_id: payload.planit_planning_mechanic_id ?? undefined
        })
      });
    }
  });
};
